import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Captions, Pause, Play, X } from "lucide-react";
import Stage from "./Stage";
import { IntroCard, createMusic } from "./Intro";
import { showApi, sentences, spokenIndex, GOLD } from "./showKit";
import { Title, Statement, Engines, Flow, Stats, Checks, Docs } from "./VisualsA";
import { Split, Ledger, Chart, Grid, Phases, Phone, Network, Calendar } from "./VisualsB";

const V = { title: Title, statement: Statement, engines: Engines, flow: Flow, stats: Stats, checks: Checks, docs: Docs, split: Split, ledger: Ledger, chart: Chart, grid: Grid, phases: Phases, phone: Phone, network: Network, calendar: Calendar };
const CLIPS = { 1: "/media/showcase/segment-1-intro.mp4" };
const HUE = ["#d4a017", "#f472b6", "#34d399", "#a78bfa", "#38bdf8", "#f59e0b", "#22d3ee", "#fb7185", "#d4a017", "#34d399", "#a78bfa", "#d4a017"];

function SegmentCard({ s }) {
  return (
    <div className="text-center">
      <motion.div initial={{ opacity: 0, letterSpacing: "1em" }} animate={{ opacity: 1, letterSpacing: "0.5em" }} transition={{ duration: 1.6 }} className="font-mono text-sm uppercase" style={{ color: GOLD }}>
        {s.act === 1 ? "Act One · Platform Capabilities" : "Act Two · Resident Impact Story"}</motion.div>
      <motion.div initial={{ opacity: 0, scale: 1.2, filter: "blur(12px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 1.4, delay: 0.3 }} className="font-serif text-[18vw] md:text-[12vw] leading-none font-black text-white/10 mt-4">{String(s.n).padStart(2, "0")}</motion.div>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.8 }} className="font-serif text-5xl md:text-7xl font-bold text-white -mt-[6vw]">{s.title}</motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.4 }} className="text-xl text-white/60 mt-5">{s.kicker}</motion.p>
    </div>
  );
}

function Caption({ text, t, dur }) {
  const parts = useMemo(() => sentences(text), [text]);
  const i = spokenIndex(parts, t, dur);
  return (
    <div className="absolute inset-x-0 bottom-[7vh] flex justify-center px-6 pointer-events-none" data-testid="show-caption">
      <AnimatePresence mode="wait">
        <motion.p key={`${text.slice(0, 12)}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3 }}
          className="max-w-4xl text-center text-xl md:text-2xl leading-snug text-white bg-black/55 backdrop-blur-md rounded-xl px-6 py-3">{parts[i] || parts[0]}</motion.p>
      </AnimatePresence>
    </div>
  );
}

export default function ShowcasePlayer({ show, segIdx, onExit, api = showApi, intro = false }) {
  const queue = useMemo(() => [...(intro ? [{ si: segIdx[0], intro: true }] : []), ...segIdx.flatMap((si) => [...(CLIPS[show.segments[si].n] ? [{ si, clip: CLIPS[show.segments[si].n] }] : []), { si, card: true }, ...show.segments[si].scenes.map((_, ci) => ({ si, ci }))])], [show, segIdx, intro]);
  const [at, setAt] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const [caps, setCaps] = useState(true);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [idle, setIdle] = useState(false);
  const urls = useRef({});
  const audio = useRef(null);
  const vid = useRef(null);
  const pad = useRef(null);
  const root = useRef(null);
  const item = queue[at];
  const seg = show.segments[item.si];
  const scene = item.card || item.clip || item.intro ? null : seg.scenes[item.ci];
  const key = (q) => `${q.si}-${q.ci}`;
  const fetchUrl = (q) => {
    if (q && !q.card && !q.clip && !q.intro && !urls.current[key(q)]) urls.current[key(q)] = api.audio(q.si, q.ci).catch(() => api.audio(q.si, q.ci)).catch(() => null);
    return q && !q.card && !q.clip && !q.intro ? urls.current[key(q)] : null;
  };
  const atRef = useRef(0);
  atRef.current = at;
  const next = useCallback(() => (atRef.current + 1 < queue.length ? setAt(atRef.current + 1) : onExit()), [queue.length, onExit]);
  const clipRef = useRef(false);
  clipRef.current = !!item.clip;
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    pad.current = createMusic();
    root.current?.requestFullscreen?.().catch(() => {});
    const onKey = (e) => {
      if (![" ", "Escape", "ArrowRight", "ArrowLeft"].includes(e.key)) return;
      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
      if (e.key === "Escape") onExit(); if (e.key === " ") setPaused((p) => !p); if (e.key === "ArrowRight" && !clipRef.current) nextRef.current(); if (e.key === "ArrowLeft") setAt((a) => Math.max(0, a - 1));
    };
    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); audio.current?.pause(); pad.current?.stop(); pad.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setT(0); setDur(0);
    audio.current?.pause();
    let timer; let alive = true;
    fetchUrl(queue[at + 1]); fetchUrl(queue[at + 2]);
    pad.current?.to(item.intro ? 0.55 : item.clip ? 0 : item.card ? 0.22 : 0.08, item.intro ? 3.4 : item.clip ? 1.6 : 1.8);
    if (item.intro) { timer = setTimeout(next, 4300); return () => clearTimeout(timer); }
    if (item.card) { timer = setTimeout(next, 4200); return () => clearTimeout(timer); }
    if (item.clip) { setPaused(false); vid.current?.play().catch(() => setPaused(true)); return undefined; }
    fetchUrl(item).then((u) => {
      if (!alive) return;
      if (!u) {
        const ms = Math.max(6000, scene.say.split(" ").length * 400); setDur(ms / 1000); let s = 0;
        timer = setInterval(() => { if (pausedRef.current) return; s += 0.2; setT(s); if (s * 1000 >= ms) { clearInterval(timer); next(); } }, 200);
        return;
      }
      const a = new Audio(u);
      audio.current = a;
      a.onloadedmetadata = () => setDur(a.duration);
      a.ontimeupdate = () => setT(a.currentTime);
      a.onended = () => { timer = setTimeout(next, 1400); };
      if (!pausedRef.current) a.play().catch(() => setPaused(true));
    });
    return () => { alive = false; clearTimeout(timer); clearInterval(timer); };
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const a = item.clip ? vid.current : audio.current; if (paused) pad.current?.pause(); else pad.current?.resume(); if (!a) return; paused ? a.pause() : a.play().catch(() => {}); }, [paused]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { let h; const move = () => { setIdle(false); clearTimeout(h); h = setTimeout(() => setIdle(true), 2500); }; move(); window.addEventListener("mousemove", move); return () => { clearTimeout(h); window.removeEventListener("mousemove", move); }; }, []);

  const Visual = scene ? V[scene.v] || Title : null;
  const sceneParts = useMemo(() => (scene ? sentences(scene.say) : []), [scene]);
  const spoken = sceneParts.slice(0, spokenIndex(sceneParts, t, dur) + 1).join(" ");
  const segScenes = queue.filter((q) => q.si === item.si && !q.card).length;
  const segPos = item.card || item.clip || item.intro ? 0 : item.ci + (dur ? Math.min(t / dur, 1) : 0);
  return createPortal(
    <div ref={root} data-testid="showcase-player" className={`fixed inset-0 z-[10000] text-white ${idle ? "cursor-none" : ""}`}>
      <Stage hue={HUE[item.si] || GOLD}>
        <div className="absolute top-[4vh] inset-x-0 h-[3px] bg-white/5"><motion.div className="h-full" style={{ background: GOLD }} animate={{ width: `${(100 * segPos) / Math.max(segScenes, 1)}%` }} transition={{ duration: 0.3 }} /></div>
        {(item.intro || item.clip) && <div className="absolute inset-0 bg-[#030408]" />}
        <AnimatePresence mode="wait">
          <motion.div key={at} data-testid={item.intro ? "show-intro-scene" : item.clip ? `show-clip-${seg.n}` : item.card ? `show-card-${seg.n}` : `show-scene-${seg.n}-${item.ci}`} className="absolute inset-0 flex items-center justify-center px-[6vw] pt-[8vh] pb-[18vh]"
            initial={{ opacity: 0, scale: 1.04, filter: "blur(14px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} exit={item.intro ? { opacity: 0, transition: { duration: 1.6, ease: "easeIn" } } : { opacity: 0, scale: 0.97, filter: "blur(10px)" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            {item.intro ? <IntroCard /> : item.clip ? (
              <div className="absolute inset-0 bg-black">
                <video ref={vid} autoPlay playsInline preload="auto" onEnded={next} onError={(e) => { const v = vid.current; if (v && (v.error || v.networkState === 3) && !e.target.src?.endsWith(".mp4")) next(); }}
                  onTimeUpdate={(e) => { setT(e.target.currentTime); setDur(e.target.duration || 0); }}
                  data-testid="show-clip-video" className="w-full h-full object-cover">
                  <source src={item.clip} type='video/mp4; codecs="avc1.640028, mp4a.40.2"' />
                  <source src={item.clip.replace(/\.mp4$/, ".webm")} type="video/webm" />
                </video>
              </div>
            ) : item.card ? <SegmentCard s={seg} /> : (
              <motion.div className="w-full flex items-center justify-center" initial={{ scale: 1, x: 0 }} animate={{ scale: 1.06, x: at % 2 ? -18 : 18 }} transition={{ duration: 45, ease: "linear" }}>
                <Visual p={scene.p || {}} data={show.data} spoken={spoken} />
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
        {scene && caps && <Caption text={scene.say} t={t} dur={dur} />}
        {seg.act === 2 && !item.card && <div className="absolute left-[3vw] top-[6vh] font-mono text-[11px] tracking-[0.25em] uppercase text-white/45" data-testid="show-illustrative">Model resident · built from real resident averages</div>}
        {!item.intro && <div className="absolute left-[3vw] bottom-[1vh] font-mono text-[10px] tracking-[0.3em] uppercase text-white/35">{seg.n} · {seg.title}</div>}
      </Stage>
      <button type="button" onClick={onExit} data-testid="show-exit" aria-label="Exit presentation" className="absolute top-[5vh] right-[2vw] z-10 w-11 h-11 grid place-items-center rounded-full bg-black/50 border border-white/20 text-white/80 hover:text-white hover:bg-black/80 transition-colors"><X className="w-5 h-5" /></button>
      <div className={`absolute bottom-[5vh] right-[2vw] z-10 flex gap-2 transition-opacity duration-500 ${idle ? "opacity-0" : "opacity-100"}`}>
        {[[ChevronLeft, () => setAt((a) => Math.max(0, a - 1)), "show-prev"], [paused ? Play : Pause, () => setPaused((p) => !p), "show-pause"], [ChevronRight, item.clip ? () => {} : next, "show-next"], [Captions, () => setCaps((c) => !c), "show-captions"]].map(([I, fn, id]) => (
          <button key={id} type="button" onClick={fn} data-testid={id} disabled={id === "show-next" && !!item.clip} className={`disabled:opacity-30 w-11 h-11 grid place-items-center rounded-full border border-white/20 bg-black/50 hover:bg-black/80 ${id === "show-captions" && !caps ? "text-white/40" : "text-white"}`}><I className="w-5 h-5" /></button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
